'use client';

// pages/details/[id].tsx

import { Footer } from '@/components/component/footer';
import { WatchDetails } from '@/components/component/details/watch-details';
import { useRouter, useParams } from 'next/navigation';
import { useEffect, useState } from 'react';
import Head from 'next/head';
import { Loader2, XCircle, SearchX } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface Watch {
  _id: string;
  name: string;
  brand: string;
  model: string;
  price: number;
  thumbnailImages: string[];
  image: string;
  vintage: boolean;
  preOwned: boolean;
  isNewWatch: boolean;
  box: boolean;
  papers: boolean;
  caseSize: number;
  lugWidth: number;
  lugToLugLength: number;
  caseThickness: number;
  description?: string;
  curatorDescription?: string;
  slug: string;
  sold: boolean;
  images?: string[];
  bandColor?: string;
  bandMaterial?: string;
  bandType?: string;
  bezelColor?: string;
  caseColor?: string;
  caseMaterial?: string;
  dialColor?: string;
  displayType?: string;
  casebackType?: string;
  gender?: string;
  waterResistance?: string;
  movement?: string;
  countryOfManufacture?: string;
  numberOfJewels?: string;
  yearManufactured?: string;
  watchShape?: string;
}

const WatchDetailsPage = () => {
  const router = useRouter();
  const params = useParams();
  const id = params?.id as string | undefined;
  const [watch, setWatch] = useState<Watch | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchWatch() {
      if (!id) {
        setError('Invalid watch ID');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const res = await fetch(`/api/watches/${id}`);
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        const response = await res.json();

        if (response.status === 'ok' && response.data) {
          setWatch(response.data);
        } else {
          throw new Error('Invalid response format');
        }
      } catch (error) {
        console.error('Error fetching watch:', error);
        setError(error instanceof Error ? error.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    }

    fetchWatch();
  }, [id]);

  const canonicalUrl = `https://watchinspect.com/details/${watch?.slug}`;

  return (
    <>
      <Head>
        <title>{watch?.name || 'Watch Details'} | Watch Inspect</title>
        <meta
          name="description"
          content={watch ? `Details for ${watch.name}` : 'Watch Details'}
        />
        <link rel="canonical" href={canonicalUrl} />
      </Head>

      <div className="min-h-screen bg-slate-900">
        <div className="relative">
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950 to-slate-900 h-24" />
          <div
            className="absolute inset-0 h-24"
            style={{
              backgroundImage:
                'radial-gradient(circle at 1px 1px, rgba(255, 255, 255, 0.15) 1px, transparent 0)',
              backgroundSize: '20px 20px',
            }}
          />
        </div>

        <main className="relative">
          {loading ? (
            <div className="min-h-[80vh] flex items-center justify-center">
              <div className="flex items-center space-x-2 text-slate-400">
                <Loader2 className="h-6 w-6 animate-spin" />
                <span>Loading watch details...</span>
              </div>
            </div>
          ) : error ? (
            <div className="min-h-[80vh] flex items-center justify-center">
              <div className="text-red-400 flex flex-col items-center space-y-4">
                <XCircle className="h-12 w-12" />
                <p>Error: {error}</p>
                <Button
                  variant="outline"
                  onClick={() => router.push('/marketplace')}
                >
                  Return to Marketplace
                </Button>
              </div>
            </div>
          ) : !watch ? (
            <div className="min-h-[80vh] flex items-center justify-center">
              <div className="text-slate-400 flex flex-col items-center space-y-4">
                <SearchX className="h-12 w-12" />
                <p>Watch not found</p>
                <Button
                  variant="outline"
                  onClick={() => router.push('/marketplace')}
                >
                  Return to Marketplace
                </Button>
              </div>
            </div>
          ) : (
            <WatchDetails watch={watch} />
          )}
        </main>

        <Footer />
      </div>
    </>
  );
};

export default WatchDetailsPage;
